import {
    Switch,
} from "@material-ui/core";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import { Edit, InfoOutlined } from '@material-ui/icons';
import moment from 'moment';
import { useRef, useState } from 'react';
import { BiCopy } from 'react-icons/bi';
import { BsCheckLg } from 'react-icons/bs';
import Tooltip from '../../../common/Tooltip';
import { getObject, getUserDetails, saveObject, toastError, toastSuccess } from '../../../utils';
import { updateUser } from './action';

const ProfilePage = () => {
    const userDetails = getUserDetails();
    const fileInputRef = useRef(null);

    const [profile, setProfile] = useState({
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        userName: userDetails?.firstName + userDetails?.lastName || '',
        email: userDetails?.email || '',
        isEmailPrivate: userDetails?.isEmailPrivate || false,
        dateOfBirth: userDetails?.dateOfBirth
            ? moment(userDetails?.dateOfBirth).isValid()
                ? moment(userDetails?.dateOfBirth).format('YYYY-MM-DD')
                : ''
            : '',
        forwardEmail: userDetails?.forwardEmail ?? '',
        profileImage: userDetails?.profileImage || userDetails?.profileImage !== "" ? userDetails?.profileImage : `https://ui-avatars.com/api/?name=${userDetails?.firstName}&background=random`
    });
    const [IsCopied, setIsCopied] = useState(false)

    const [editableFields, setEditableFields] = useState({
        firstName: profile.firstName,
        lastName: profile.lastName,
        dateOfBirth: profile.dateOfBirth,
        isEmailPrivate: profile.isEmailPrivate,
    });

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditableFields({
            ...editableFields,
            [name]: value
        });
    };

    const handleSave = () => {
        const formData = new FormData();
        formData.append('firstName', editableFields.firstName);
        formData.append('lastName', editableFields.lastName);
        formData.append('isEmailPrivate', editableFields.isEmailPrivate);
        formData.append('dateOfBirth', editableFields.dateOfBirth);

        if (fileInputRef.current.files[0]) {
            formData.append('files', fileInputRef.current.files[0]);
        }

        updateUser(formData)
            .then((res) => {
                setProfile((prevProfile) => ({
                    ...prevProfile,
                    firstName: editableFields.firstName,
                    lastName: editableFields.lastName,
                    dateOfBirth: editableFields.dateOfBirth,
                    isEmailPrivate: editableFields.isEmailPrivate,
                    profileImage: fileInputRef.current.files[0] ? URL.createObjectURL(fileInputRef.current.files[0]) : prevProfile.profileImage
                }));
                const prevData = getObject('auth')
                saveObject("auth", { ...prevData, ...res.data })
                toastSuccess('User saved successfully')
            })
            .catch((err) => {
                console.error('Error updating user details:', err);
                toastError('Error updating user details')
            });
    };

    const handleProfileImageClick = () => {
        fileInputRef.current.click();
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setProfile((prevProfile) => ({
                    ...prevProfile,
                    profileImage: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(profile.forwardEmail)
            .then(() => {
                console.log('Email copied to clipboard');
                setIsCopied(true)
                setTimeout(() => {
                    setIsCopied(false)
                }, 2000);
            })
            .catch((err) => {
                console.error('Failed to copy email: ', err);
            });
    };

    const [open, setOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <div className="flex justify-center items-center min-h-[calc(100vh-106px)] px-2 sm:px-6 w-full">
                <div className="max-w-3xl w-full bg-white shadow-lg rounded-lg overflow-hidden h-fit">

                    <div className="flex flex-col items-center text-white text-center py-5 sm:py-10 bg-gray-50">
                        <div className="relative group">
                            <img
                                src={profile.profileImage}
                                alt="Profile Avatar"
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-lg group-hover:scale-105 transition-transform duration-300 cursor-pointer object-cover"
                                onClick={handleProfileImageClick}
                            />
                            <div
                                className="absolute shadow-md top-0 right-0 bg-white p-2 rounded-full transform translate-x-1 translate-y-1 group-hover:scale-110 transition-all cursor-pointer"
                                onClick={handleProfileImageClick}
                            >
                                <Edit className="w-5 h-5 text-gray-700" />
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleProfileImageChange}
                                className="hidden"
                            />
                        </div>
                        <h1 className="text-xl sm:text-2xl font-bold text-black mt-3 sm:mt-4">{`${profile.firstName} ${profile.lastName}`}</h1>
                        <p className="text-sm text-gray-600 mt-1">{profile.email}</p>
                    </div>

                    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={editableFields.firstName}
                                    onChange={handleEditChange}
                                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={editableFields.lastName}
                                    onChange={handleEditChange}
                                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                                <input
                                    type="text"
                                    value={profile.userName}
                                    disabled
                                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                                    <span>
                                        Email
                                    </span>
                                    <div className="flex items-center">
                                        <Tooltip text="If email is private, it will not be visible to other users in message box">
                                            <InfoOutlined className='w-5 h-5 mr-1' color='primary' />
                                        </Tooltip>
                                        <label className="text-sm font-medium text-gray-700 mr-2">Make Email Private</label>
                                        <Switch
                                            checked={editableFields.isEmailPrivate}
                                            onChange={() => setEditableFields({ ...editableFields, isEmailPrivate: !editableFields.isEmailPrivate })}
                                            color="primary"
                                        />
                                    </div>
                                </label>

                                <input
                                    type="email"
                                    value={profile.email}
                                    disabled
                                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                                />
                            </div>

                            <div>
                                <label
                                    className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer flex items-center"
                                    onClick={handleOpen}
                                >
                                    Forward Email
                                    <span
                                        className="ml-1 flex items-center justify-center text-[#2196f3]"
                                    >
                                        <InfoOutlined style={{ fontSize: '1.2rem' }} />
                                    </span>
                                </label>

                                <div className="flex items-center w-full">
                                    <input
                                        type="email"
                                        value={profile.forwardEmail}
                                        disabled
                                        className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed truncate"
                                    />
                                    {IsCopied ? (
                                        <BsCheckLg className="ml-2 cursor-pointer text-green-600 text-xl flex-shrink-0" />
                                    ) : (
                                        <BiCopy
                                            onClick={handleCopyEmail}
                                            className="ml-2 cursor-pointer text-[#313C4C] flex-shrink-0"
                                            size={24}
                                        />
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={editableFields.dateOfBirth}
                                    onChange={handleEditChange}
                                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                            </div>

                        </div>

                        <div className="pt-4">
                            <button
                                onClick={handleSave}
                                className="w-full px-8 py-2 bg-[#313C4C] text-white rounded-md hover:bg-sky-800 transition duration-200 shadow-sm font-medium"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <CustomAddModal
                open={open}
                title="Email Forwarding Instructions"
                saveLabel="Close"
                buttonCase="capitalize"
                saveSpotlight=""
                onSubmitConfirm={handleClose}
                handleClose={handleClose}
                onClose={handleClose}
            >
                <div className="mb-3">
                    <h4 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider border-l-4 border-l-[#313C4C] pl-2 leading-none py-0.5 m-0 mb-1">
                        Select Your Email Provider
                    </h4>
                    <p className="text-[11px] text-slate-600 m-0">
                        Click on the links below for specific forwarding instructions
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                    {[
                        { name: 'Gmail', url: 'https://support.google.com/a/answer/4524505' },
                        { name: 'Outlook', url: 'https://support.microsoft.com/en-us/office/turn-on-automatic-forwarding-in-outlook-7f2670a1-7fff-4475-8a3c-5822d63b0c8e' },
                        { name: 'Yahoo', url: 'https://help.yahoo.com/kb/SLN29133.html' }
                    ].map((provider) => (
                        <a
                            key={provider.name}
                            href={provider.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-7 px-3 text-[11px] font-normal text-slate-900 bg-white hover:bg-slate-100 border border-slate-300 rounded-none flex items-center justify-center transition-colors cursor-pointer no-underline"
                        >
                            {provider.name}
                        </a>
                    ))}
                </div>

                <div className="bg-[#f8fafc] border border-slate-200 p-2 flex gap-2 items-start">
                    <InfoOutlined className="!w-4 !h-4 text-[#313C4C] flex-shrink-0" />
                    <p className="text-[11px] text-slate-600 leading-relaxed m-0">
                        If your email provider is not listed, please contact your email provider and set forwarding to:
                        <span className="font-medium ml-1 text-slate-900 break-all">
                            {userDetails?.forwardEmail ?? 'Not specified'}
                        </span>
                    </p>
                </div>
            </CustomAddModal>
        </>
    );
};

export default ProfilePage;